import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CopyButton from '../../common/CopyButton';
import DisabledFieldset from '../../common/DisabledFieldset';
import RefreshIcon from '@mui/icons-material/Refresh';
import { manageExternalConnection } from '../../../services/externalSources';
import { identifierNameError } from '../../../utils/identifierNames';

export const DIALECT_AUTH_METHODS = {
  bigquery: ['service_account_key', 'decode_principal'],
  postgresql: ['user_password'],
  snowflake: ['user_password', 'key_pair'],
  databricks: ['access_token'],
};

const DIALECT_LABELS = {
  bigquery: 'BigQuery',
  postgresql: 'Postgres',
  snowflake: 'Snowflake',
  databricks: 'Databricks',
};

const AUTH_METHOD_LABELS = {
  service_account_key: 'Service account key',
  decode_principal: 'Decode principal',
  user_password: 'User and password',
  key_pair: 'Key pair',
  access_token: 'Access token',
};

const CONFIG_FIELDS = {
  bigquery: [
    { key: 'project', label: 'Project', required: true },
    { key: 'dataset', label: 'Dataset', required: true },
  ],
  postgresql: [
    { key: 'host', label: 'Host', required: true },
    { key: 'port', label: 'Port', required: true, type: 'number', defaultValue: 5432 },
    { key: 'database', label: 'Database', required: true },
    { key: 'schema', label: 'Schema', required: false },
    { key: 'sslmode', label: 'SSL mode', required: true, defaultValue: 'require' },
  ],
  snowflake: [
    { key: 'account', label: 'Account', required: true },
    { key: 'database', label: 'Database', required: true },
    { key: 'schema', label: 'Schema', required: true },
    { key: 'warehouse', label: 'Warehouse', required: true },
    { key: 'role', label: 'Role', required: false },
  ],
  databricks: [
    { key: 'server_hostname', label: 'Server hostname', required: true },
    { key: 'http_path', label: 'HTTP path', required: true },
    { key: 'catalog', label: 'Catalog', required: true },
    { key: 'schema', label: 'Schema', required: true },
  ],
};

const SECRET_FIELDS = {
  service_account_key: [
    { key: 'service_account_key_json', label: 'Service account key JSON', required: true, multiline: true, rows: 8 },
  ],
  user_password: [
    { key: 'user', label: 'User', required: true },
    { key: 'password', label: 'Password', required: true, type: 'password' },
  ],
  key_pair: [
    { key: 'user', label: 'User', required: true },
    { key: 'private_key_pem', label: 'Private key PEM', required: true, multiline: true, rows: 8 },
    { key: 'private_key_passphrase', label: 'Private key passphrase', required: false, type: 'password' },
  ],
  access_token: [
    { key: 'access_token', label: 'Access token', required: true, type: 'password' },
  ],
  decode_principal: [],
};

function defaultConfigForDialect(dialect, existingConfig = {}) {
  return (CONFIG_FIELDS[dialect] || []).reduce((acc, field) => {
    acc[field.key] = existingConfig[field.key] ?? field.defaultValue ?? '';
    return acc;
  }, {});
}

function normalizeConfig(dialect, config) {
  return (CONFIG_FIELDS[dialect] || []).reduce((acc, field) => {
    const value = config[field.key];
    if (value === '' || value == null) {
      // A blank field is sent as null so the backend removes the stored
      // value; omitting it would keep whatever was saved before. validate()
      // has already refused a blank required field.
      acc[field.key] = null;
      return acc;
    }
    acc[field.key] = field.type === 'number' ? Number(value) : value;
    return acc;
  }, {});
}

function buildSecret(authMethod, secretFields) {
  if (authMethod === 'decode_principal') return null;
  if (authMethod === 'service_account_key') {
    const raw = secretFields.service_account_key_json?.trim();
    try {
      return { service_account_key: JSON.parse(raw) };
    } catch (_) {
      throw new Error('Service account key must be valid JSON');
    }
  }
  if (authMethod === 'user_password') {
    return {
      user: secretFields.user,
      password: secretFields.password,
    };
  }
  if (authMethod === 'key_pair') {
    return {
      user: secretFields.user,
      private_key_pem: secretFields.private_key_pem,
      ...(secretFields.private_key_passphrase ? { private_key_passphrase: secretFields.private_key_passphrase } : {}),
    };
  }
  if (authMethod === 'access_token') {
    return { access_token: secretFields.access_token };
  }
  return null;
}

function GrantInstructions({ instructions, onRetest, retesting }) {
  const grants = Array.isArray(instructions?.grants) ? instructions.grants : [];

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle2">Grant instructions</Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={onRetest} disabled={retesting}>
            {retesting ? 'Testing...' : 'Re-test'}
          </Button>
        </Box>
        {!instructions && (
          <Typography variant="body2" color="text.secondary">
            {retesting ? 'Loading the grant instructions...' : 'Re-test to load the grant instructions.'}
          </Typography>
        )}
        {instructions?.principal && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Principal</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{instructions.principal}</Typography>
            </Box>
            <CopyButton text={instructions.principal} />
          </Box>
        )}
        {grants.map((grant) => (
          <Box key={`${grant.label}-${grant.command}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">{grant.label}</Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre-line', wordBreak: 'break-word' }}
              >
                {grant.command}
              </Typography>
            </Box>
            <CopyButton text={grant.command} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function ConnectionDialog({ open, connection, onClose }) {
  const isEdit = Boolean(connection);
  const [name, setName] = useState('');
  const [dialect, setDialect] = useState('bigquery');
  const [authMethod, setAuthMethod] = useState('service_account_key');
  const [config, setConfig] = useState(defaultConfigForDialect('bigquery'));
  const [secretFields, setSecretFields] = useState({});
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [grantInstructions, setGrantInstructions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retesting, setRetesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const nextDialect = connection?.dialect || 'bigquery';
    const legalMethods = DIALECT_AUTH_METHODS[nextDialect] || [];
    const nextAuth = connection?.auth_method && legalMethods.includes(connection.auth_method)
      ? connection.auth_method
      : legalMethods[0];

    setName(connection?.name || '');
    setDialect(nextDialect);
    setAuthMethod(nextAuth);
    setConfig(defaultConfigForDialect(nextDialect, connection?.config || {}));
    setSecretFields({});
    setError('');
    setResult(null);
    setGrantInstructions(null);
    // A test started by an earlier open may still be in flight; its response
    // is ignored (cancelled below), so the lock it holds is released here.
    setRetesting(false);

    if (nextAuth !== 'decode_principal' || !connection?.id) return undefined;
    // A Decode-principal connection only works once the customer has granted
    // Decode's broker access on their side, so reopening one shows the grant
    // steps again and whether they have taken effect (M44).
    let cancelled = false;
    setRetesting(true);
    manageExternalConnection({ action: 'test', connection_id: connection.id })
      .then((response) => {
        if (cancelled) return;
        setResult(response);
        setGrantInstructions(response?.grant_instructions || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to test connection');
      })
      .finally(() => {
        if (!cancelled) setRetesting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, connection]);

  const legalAuthMethods = useMemo(() => DIALECT_AUTH_METHODS[dialect] || [], [dialect]);
  const configFields = CONFIG_FIELDS[dialect] || [];
  const secretFieldDefs = SECRET_FIELDS[authMethod] || [];

  const handleDialectChange = (nextDialect) => {
    const nextAuth = (DIALECT_AUTH_METHODS[nextDialect] || [])[0] || '';
    setDialect(nextDialect);
    setAuthMethod(nextAuth);
    setConfig(defaultConfigForDialect(nextDialect));
    setSecretFields({});
    setGrantInstructions(null);
  };

  const handleAuthMethodChange = (nextAuth) => {
    setAuthMethod(nextAuth);
    setSecretFields({});
    setGrantInstructions(null);
  };

  const validate = () => {
    if (!isEdit) {
      const nameError = identifierNameError(name, 'Connection name');
      if (nameError) return nameError;
    }
    const missingConfig = configFields.find((field) => field.required && (config[field.key] === '' || config[field.key] == null));
    if (missingConfig) return `${missingConfig.label} is required`;
    const authChanged = isEdit && authMethod !== connection?.auth_method;
    if (!isEdit || authChanged || Object.values(secretFields).some((value) => value !== '' && value != null)) {
      const missingSecret = secretFieldDefs.find((field) => field.required && !secretFields[field.key]?.trim());
      if (missingSecret) return `${missingSecret.label} is required`;
    }
    return '';
  };

  const handleSubmit = async () => {
    if (saving || retesting) return;
    setError('');
    setResult(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      const secretHasValues = Object.values(secretFields).some((value) => value !== '' && value != null);
      const body = {
        action: isEdit ? 'update' : 'create',
        ...(isEdit ? { connection_id: connection.id } : { name: name.trim() }),
        dialect,
        auth_method: authMethod,
        config: normalizeConfig(dialect, config),
        ...((!isEdit || secretHasValues) && authMethod !== 'decode_principal'
          ? { secret: buildSecret(authMethod, secretFields) }
          : {}),
      };

      const response = await manageExternalConnection(body);
      setSecretFields({});
      setResult(response);
      setGrantInstructions(response?.grant_instructions || null);
    } catch (err) {
      setError(err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  const handleRetest = async () => {
    if (saving || retesting) return;
    const id = connection?.id || result?.connection_id;
    if (!id) return;
    setError('');
    try {
      setRetesting(true);
      const response = await manageExternalConnection({ action: 'test', connection_id: id });
      setResult(response);
      if (response?.grant_instructions) setGrantInstructions(response.grant_instructions);
    } catch (err) {
      setError(err.message || 'Failed to test connection');
    } finally {
      setRetesting(false);
    }
  };

  // The grant panel belongs to a saved Decode-principal connection: one just
  // saved as such (the response carried the steps) or one reopened while it
  // still uses that method. Switching the method away hides it until saved.
  const showGrantInstructions = authMethod === 'decode_principal'
    && (Boolean(grantInstructions) || (isEdit && connection?.auth_method === 'decode_principal'));

  // The dialog is one instance reused for every connection, and a save or re-test writes
  // its response into whatever form is open when it lands — so the dialog cannot be
  // dismissed (button, backdrop or Escape) while one is in flight.
  const busy = saving || retesting;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'Edit Source Connection' : 'New Source Connection'}</DialogTitle>
      <DialogContent dividers>
        <DisabledFieldset disabled={busy}>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {result && (
              <Alert severity={result.ok ? 'success' : 'warning'}>
                {result.ok
                  ? 'Connection test passed.'
                  : (result.error?.message || 'Connection saved, but the test did not pass.')}
              </Alert>
            )}

            <TextField
              label="Connection name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              disabled={isEdit}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Dialect</InputLabel>
                <Select
                  label="Dialect"
                  value={dialect}
                  onChange={(e) => handleDialectChange(e.target.value)}
                  disabled={isEdit}
                >
                  {Object.keys(DIALECT_AUTH_METHODS).map((value) => (
                    <MenuItem key={value} value={value}>{DIALECT_LABELS[value]}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Auth method</InputLabel>
                <Select
                  label="Auth method"
                  value={authMethod}
                  onChange={(e) => handleAuthMethodChange(e.target.value)}
                >
                  {legalAuthMethods.map((value) => (
                    <MenuItem key={value} value={value}>{AUTH_METHOD_LABELS[value]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Divider />
            <Typography variant="subtitle2">Connection config</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
              {configFields.map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  type={field.type || 'text'}
                  value={config[field.key] ?? ''}
                  onChange={(e) => setConfig((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  required={field.required}
                  sx={{ flex: '1 1 220px' }}
                />
              ))}
            </Stack>

            {secretFieldDefs.length > 0 ? (
              <>
                <Divider />
                <Typography variant="subtitle2">Secret fields</Typography>
                <Stack spacing={2}>
                  {secretFieldDefs.map((field) => (
                    <TextField
                      key={field.key}
                      label={field.label}
                      type={field.type || 'text'}
                      value={secretFields[field.key] || ''}
                      onChange={(e) => setSecretFields((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      required={field.required && !isEdit}
                      multiline={field.multiline}
                      rows={field.rows}
                      helperText={isEdit ? 'Leave blank to keep the existing secret.' : undefined}
                      fullWidth
                    />
                  ))}
                </Stack>
              </>
            ) : (
              <Alert severity="info">
                This method has no secret fields. Decode will connect with its broker service account after the customer grants access.
              </Alert>
            )}

            {showGrantInstructions && (
              <GrantInstructions
                instructions={grantInstructions}
                onRetest={handleRetest}
                retesting={retesting}
              />
            )}
          </Stack>
        </DisabledFieldset>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={busy}>
          {saving ? 'Saving...' : 'Save Connection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConnectionDialog;
